/**
 * Step 1 — identity: name, color (presets + any custom color), and for a
 * container the master config of its campaign group (shown, never picked)
 * plus the campaign it runs on (one of the group's), or for a master config
 * its campaign group; then the static tag stamped on every draft.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useMemo, useState } from 'react';
import { Bookmark, Megaphone, Palette, Plus, Tag } from 'lucide-react';

import { HintDot, Input, SearchableSelect } from '@/components/ui';
import { useStaticTagOptions } from '@/features/flow-banks/lib/use-static-tag-options';
import { StaticTagFormModal } from '@/pages/admin/TagRulesPage/components/StaticTagFormModal';
import { getApiErrorMessage } from '@/lib/utils';
import { useCreateStaticTag } from '@/hooks/smswiser/static-tags/useStaticTags';
import { useCountry } from '@/hooks/smswiser/core/useSMSWiser';
import { useToast } from '@/providers/ToastProvider';

import { CONTAINER_COLOR_PRESETS } from '../../../drop-generator.types';
import { useCampaignGroups } from '../../campaign/use-campaign-groups';
import { GroupCampaignPicker } from '../master/group/GroupCampaignPicker';
import { MasterField } from '../master/group/MasterField';
import type { ContainerEditor } from '../state/useContainerEditor';
import { FIELD_ICON, FIELD_LABEL } from './fields/field-chrome';

interface Props {
  editor: ContainerEditor;
  countryKey: string;
  /** A master config shows its campaign group; a container its group's master config + campaign. */
  mode?: 'container' | 'master';
  /** A master config opened from a campaign: its campaign is shown, never picked. */
  campaignLocked?: boolean;
}

export { FIELD_LABEL };

export const ContainerStep: FC<Props> = ({ editor, countryKey, mode = 'container', campaignLocked = false }) => {
  const country = useCountry(countryKey);
  const { tagOptions } = useStaticTagOptions();
  const createStaticTag = useCreateStaticTag();
  const { success, error } = useToast();
  const ix = useCampaignGroups(countryKey);
  const { draft, setField } = editor;
  const [createTagOpen, setCreateTagOpen] = useState(false);

  const handleCreateStaticTag = async (values: { name: string; color: string }) => {
    try {
      const created = await createStaticTag.mutateAsync(values);
      setField('staticTagId', created.id);
      setCreateTagOpen(false);
      success('Static tag created', `${created.name} is selected for this container.`);
    } catch (err) {
      error('Could not create static tag', getApiErrorMessage(err));
    }
  };

  const campaignOptions = useMemo(
    () => (country.data?.campaigns ?? []).map((c) => ({ value: String(c.id), label: c.name })),
    [country.data?.campaigns],
  );
  const isPreset = (CONTAINER_COLOR_PRESETS as readonly string[]).includes(draft.color);

  return (
    <div className="flex flex-wrap items-start gap-x-8 gap-y-4">
      <div className="flex w-60 flex-col gap-1.5">
        <span className={FIELD_LABEL}>
          <Tag className={FIELD_ICON} /> Name / tag
        </span>
        <Input
          value={draft.name}
          onChange={(e) => setField('name', e.target.value)}
          placeholder={mode === 'master' ? 'e.g. AU casino config' : 'e.g. WARM UP'}
          className="h-10"
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <span className={FIELD_LABEL}>
          <Palette className={FIELD_ICON} /> Color
        </span>
        <div className="flex h-10 items-center gap-1.5">
          {CONTAINER_COLOR_PRESETS.map((color) => (
            <button
              key={color}
              type="button"
              aria-label={`Color ${color}`}
              onClick={() => setField('color', color)}
              className={`h-5 w-5 rounded-full ${draft.color === color ? 'ring-2 ring-ring ring-offset-2 ring-offset-background' : ''}`}
              style={{ backgroundColor: color }}
            />
          ))}
          <label
            className={`relative h-5 w-5 cursor-pointer rounded-full ${!isPreset ? 'ring-2 ring-ring ring-offset-2 ring-offset-background' : ''}`}
            style={{ background: !isPreset ? draft.color : 'conic-gradient(red, yellow, lime, cyan, blue, magenta, red)' }}
            title="Any custom color"
          >
            <input
              type="color"
              value={draft.color}
              onChange={(e) => setField('color', e.target.value)}
              className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
              aria-label="Custom color"
            />
          </label>
        </div>
      </div>
      {mode === 'container' ? (
        <div className="flex min-w-[18rem] max-w-2xl grow basis-[22rem] flex-col gap-1.5">
          <MasterField editor={editor} countryKey={countryKey} />
        </div>
      ) : null}
      {mode === 'container' ? (
        <div className="flex min-w-[16rem] max-w-xl grow basis-72 flex-col gap-1.5">
          <GroupCampaignPicker editor={editor} countryKey={countryKey} />
        </div>
      ) : null}
      {mode === 'master' ? (
        <div className="flex min-w-[16rem] max-w-xl grow basis-72 flex-col gap-1.5">
          <span className={FIELD_LABEL}>
            <Megaphone className={FIELD_ICON} /> Campaign group
          </span>
          {campaignLocked && draft.campaignWiserId ? (
            <span
              className="flex h-10 min-w-0 items-center gap-2 rounded-md border border-border/60 bg-muted/30 px-3 text-sm"
              title={ix.group(ix.keyOf(draft.campaignWiserId))?.fullLabel ?? 'A master config belongs to its campaign group'}
            >
              <span className="truncate font-medium">{ix.labelOf(ix.keyOf(draft.campaignWiserId))}</span>
              <span className="shrink-0 text-xs text-muted-foreground">
                · {ix.group(ix.keyOf(draft.campaignWiserId))?.campaigns.length ?? 1} campaigns · the group open on the page
              </span>
            </span>
          ) : (
            <SearchableSelect
              options={campaignOptions}
              value={draft.campaignWiserId ? String(draft.campaignWiserId) : ''}
              onChange={(value) => setField('campaignWiserId', Number(value))}
              placeholder={campaignOptions.length === 0 ? 'No campaigns in this country' : 'Pick a campaign…'}
            />
          )}
        </div>
      ) : null}
      {mode === 'container' ? (
        <div className="flex min-w-[14rem] basis-60 flex-col gap-1.5">
          <span className={FIELD_LABEL}>
            <Bookmark className={FIELD_ICON} /> Static tag
            <HintDot label="Stamped on every draft this container generates, so the drops list can be filtered by container. Managed under Tag Rules → Static." />
          </span>
          <div className="flex min-w-0 items-center gap-2">
            <div className="min-w-0 flex-1">
              <SearchableSelect
                options={tagOptions}
                value={draft.staticTagId ?? ''}
                onChange={(value) => setField('staticTagId', value === '' ? null : String(value))}
                placeholder="No tag"
                searchPlaceholder="Search tags…"
              />
            </div>
            <button
              type="button"
              aria-label="Create static tag"
              title="Create static tag"
              onClick={() => setCreateTagOpen(true)}
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-input bg-background text-muted-foreground transition-colors hover:border-primary/50 hover:bg-primary/5 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
          <StaticTagFormModal
            open={createTagOpen}
            onOpenChange={setCreateTagOpen}
            onSubmit={handleCreateStaticTag}
            submitting={createStaticTag.isPending}
          />
        </div>
      ) : null}
    </div>
  );
};
